import { LateralConnection, GullyConnection, PipeRun, Manhole, StormSewerRun } from '../types/tender.types';

/**
 * Calculate the connection depth for a lateral or gully at the main sewer
 */
export function calculateConnectionDepth(
  connection: LateralConnection | GullyConnection,
  manholes: Manhole[],
  pipeRuns: (PipeRun | StormSewerRun)[]
): number {
  if (connection.connectionType === 'direct_to_manhole') {
    const manhole = manholes.find(m => m.ref === connection.toManhole);
    if (!manhole) return 0;
    
    // 150mm above manhole invert level
    return manhole.invertLevel - 0.15;
  } else if (connection.connectionType === 'junction_on_run') {
    const run = pipeRuns.find(r => r.id === connection.toPipeRun);
    if (!run || !connection.connectionChainage) return 0;
    
    // Interpolate depth at the chainage point
    const interpolatedDepth = interpolateDepthAtChainage(run, connection.connectionChainage, manholes);
    
    // 150mm above pipe invert
    return interpolatedDepth - 0.15;
  }
  
  return 0;
}

/**
 * Interpolate the invert depth at a specific chainage along a pipe run
 */
function interpolateDepthAtChainage(
  run: PipeRun | StormSewerRun,
  chainage: number,
  manholes: Manhole[]
): number {
  const fromMH = manholes.find(m => m.ref === run.fromMH);
  const toMH = manholes.find(m => m.ref === run.toMH);
  
  if (!fromMH || !toMH) return 0;
  
  // Calculate the interpolated invert level
  const gradient = (toMH.invertLevel - fromMH.invertLevel) / run.length;
  const interpolatedInvert = fromMH.invertLevel + (gradient * chainage);
  
  // Return invert level (negative because deeper is lower elevation)
  return interpolatedInvert;
}

/**
 * Check if a lateral connection requires a first-off manhole
 */
export function checkFirstOffRequired(lateral: LateralConnection): boolean {
  // First-off required if:
  // - Average depth > 1.2m
  // - Special conditions apply
  
  const avgDepth = lateral.averageDepth || 0;
  if (avgDepth > 1.2) {
    lateral.requiresFirstOff = true;
    lateral.firstOffType = avgDepth > 2.0 ? 'concrete' : 'plastic';
    return true;
  }
  
  lateral.requiresFirstOff = false;
  lateral.firstOffType = undefined;
  return false;
}

/**
 * Calculate the depth category for pricing (rounded to nearest 0.25m)
 */
export function calculateDepthCategory(depth: number): number {
  return Math.round(depth / 0.25) * 0.25;
}

/**
 * Generate default lateral connections for a range of plots
 */
export function generateDefaultLaterals(
  plotStart: number,
  plotEnd: number,
  defaultLength: number = 6,
  defaultDiameter: string = '100mm',
  defaultInvertDepth: number = 0.6,
  defaultManholeDiameter: string = '1200mm',
  defaultBackdrop: number = 0
): LateralConnection[] {
  const laterals: LateralConnection[] = [];
  
  for (let plotNum = plotStart; plotNum <= plotEnd; plotNum++) {
    laterals.push({
      id: Date.now().toString() + '_' + plotNum,
      ref: `L${plotNum}`,
      connectionType: 'direct_to_manhole',
      length: defaultLength,
      pipeDiameter: defaultDiameter,
      manholeDiameter: defaultManholeDiameter,
      backdrop: defaultBackdrop,
      depth: defaultInvertDepth,
      requiresFirstOff: false,
      rockers: 2
    });
  }
  
  return laterals;
}

/**
 * Auto-assign lateral connections to nearest suitable manholes or runs
 */
export function autoAssignConnections(
  laterals: LateralConnection[],
  manholes: Manhole[],
  pipeRuns: (PipeRun | StormSewerRun)[],
  sewerType: 'foul' | 'storm'
): LateralConnection[] {
  return laterals.map(lateral => {
    // Find suitable manholes (foul/both for foul sewers, storm/both for storm sewers)
    const suitableManholes = manholes.filter(mh => 
      mh.type === sewerType || mh.type === 'both'
    );
    
    if (suitableManholes.length > 0) {
      // For simplicity, assign to first available manhole
      // In a real implementation, this would consider proximity
      lateral.toManhole = suitableManholes[0].ref;
      lateral.connectionType = 'direct_to_manhole';
    }
    
    return lateral;
  });
}

/**
 * Validate lateral and gully connections
 */
export function validateConnections(
  connections: (LateralConnection | GullyConnection)[],
  manholes: Manhole[],
  pipeRuns: (PipeRun | StormSewerRun)[],
  sewerType: 'foul' | 'storm'
): string[] {
  const errors: string[] = [];
  
  connections.forEach((connection, index) => {
    // Check connection references exist
    if (connection.connectionType === 'direct_to_manhole') {
      if (!connection.toManhole) {
        errors.push(`Connection ${index + 1}: No manhole specified`);
      } else {
        const manhole = manholes.find(m => m.ref === connection.toManhole);
        if (!manhole) {
          errors.push(`Connection ${index + 1}: Manhole ${connection.toManhole} not found`);
        } else {
          // Check manhole type compatibility
          if (sewerType === 'foul' && manhole.type === 'storm') {
            errors.push(`Connection ${index + 1}: Cannot connect to storm-only manhole ${connection.toManhole}`);
          }
          if (sewerType === 'storm' && manhole.type === 'foul') {
            errors.push(`Connection ${index + 1}: Cannot connect gully to foul-only manhole ${connection.toManhole}`);
          }
        }
      }
    } else if (connection.connectionType === 'junction_on_run') {
      if (!connection.toPipeRun) {
        errors.push(`Connection ${index + 1}: No pipe run specified`);
      } else {
        const run = pipeRuns.find(r => r.id === connection.toPipeRun);
        if (!run) {
          errors.push(`Connection ${index + 1}: Pipe run not found`);
        } else if (!connection.connectionChainage) {
          errors.push(`Connection ${index + 1}: No chainage specified for junction`);
        } else if (connection.connectionChainage > run.length) {
          errors.push(`Connection ${index + 1}: Chainage ${connection.connectionChainage}m exceeds run length ${run.length}m`);
        }
      }
    }
    
    // Check reasonable connection lengths
    if (connection.length > 15) {
      errors.push(`Connection ${index + 1}: Unusually long connection (${connection.length}m)`);
    }
    
    // Basic validation passed
  });
  
  return errors;
}

/**
 * Calculate lateral and gully connection summaries
 */
export function calculateConnectionSummary(connections: (LateralConnection | GullyConnection)[]) {
  const summary = {
    totalConnections: connections.length,
    averageLength: 0,
    depthCategories: new Map<number, number>(),
    firstOffManholes: 0,
    plasticFirstOffs: 0,
    concreteFirstOffs: 0
  };
  
  if (connections.length === 0) return summary;
  
  let totalLength = 0;
  
  connections.forEach(connection => {
    totalLength += connection.length;
    
    // Count by average depth category
    if ('avgDepthCategory' in connection && connection.avgDepthCategory) {
      const category = connection.avgDepthCategory;
      summary.depthCategories.set(category, (summary.depthCategories.get(category) || 0) + 1);
    }
    
    // Count first-off manholes if it's a lateral
    if ('requiresFirstOff' in connection && connection.requiresFirstOff) {
      summary.firstOffManholes++;
      if (connection.firstOffType === 'plastic') {
        summary.plasticFirstOffs++;
      } else if (connection.firstOffType === 'concrete') {
        summary.concreteFirstOffs++;
      }
    }
  });
  
  summary.averageLength = totalLength / connections.length;
  
  return summary;
}